import { cur } from '@/lib/currency';
import { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { BranchZatcaFields, BRANCH_EXTRA_KEYS } from '@/components/branches/BranchZatcaFields';
import { StorefrontPreviewPanel } from '@/components/branches/StorefrontPreviewPanel';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit, Trash2, Eye, ShoppingCart, Receipt, Truck, AlertTriangle, Globe, DollarSign, Percent, Store, Crown, Building2, Coins, Wallet, GitCompare, Brain, ChevronLeft, ChevronRight, Sparkles, CalendarDays } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Checkbox } from '@/components/ui/checkbox';
import PageTour from '@/components/PageTour';
import BranchCard from '@/components/BranchCard';
import BranchWizardDialog from '@/components/branches/BranchWizardDialog';
import api from '@/lib/api';
import { useBranchStore } from '@/stores';
// iter689 — Shared country preset registry. Used to render a flag
// chip next to each branch in the compare picker, giving multi-
// country chains a fast visual scan of which branch is where.
import { getCountry } from '@/lib/countries';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { localToday, toLocalYmd } from '@/lib/dateRange';
import { optimiseThermalLogo, formatBytes } from '@/lib/thermalLogoOptimizer';

const COUNTRIES = [
  { code: 'SA', name: 'Saudi Arabia' }, { code: 'AE', name: 'UAE' },
  { code: 'KW', name: 'Kuwait' }, { code: 'QA', name: 'Qatar' },
  { code: 'BH', name: 'Bahrain' }, { code: 'OM', name: 'Oman' },
  { code: 'EG', name: 'Egypt' }, { code: 'JO', name: 'Jordan' },
  { code: 'IN', name: 'India' }, { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' }, { code: 'PK', name: 'Pakistan' },
  { code: 'PH', name: 'Philippines' }, { code: 'BD', name: 'Bangladesh' },
];

const CURRENCIES = [
  'SAR', 'AED', 'USD', 'EUR', 'GBP', 'INR', 'PHP', 'PKR', 'BDT',
  'IDR', 'NPR', 'BRL', 'MXN', 'EGP', 'QAR', 'KWD', 'BHD', 'OMR', 'JOD',
];

const emptyForm = { name: '', location: '', country_code: '', currency_code: '', tax_rate: '', tax_name: '', default_payment_mode: 'bank', online_price_markup_percent: '', business_type: 'restaurant', branch_logo_url: '' };

export default function BranchesPage() {
  const { t } = useLanguage();
  const { branches, fetchBranches } = useBranchStore();
  const [summaries, setSummaries] = useState({});
  const [summaryWindow, setSummaryWindow] = useState(null); // {start_date, end_date}
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  // iter618 — One-click "Open new branch" wizard. Simpler than the
  // full "Add Branch" form: just name + country, everything else
  // auto-derived from country defaults.
  const [showWizard, setShowWizard] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  // iter923 — Bank History timeline data (date-based bank switching, iter922).
  const [bankAccounts, setBankAccounts] = useState([]);
  useEffect(() => {
    api.get('/bank-accounts').then(r => setBankAccounts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  const bankTimeline = (branchId) => {
    const linked = bankAccounts
      .filter(a => {
        const ids = (a.branch_ids && a.branch_ids.length) ? a.branch_ids : (a.branch_id ? [a.branch_id] : []);
        return ids.includes(branchId);
      })
      .sort((a, b) => String(a.active_from || '').localeCompare(String(b.active_from || '')));
    return linked.map((a, i) => ({ ...a, until: linked[i + 1]?.active_from || null }));
  };
  const [editingBranch, setEditingBranch] = useState(null);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [countryDefaults, setCountryDefaults] = useState({});

  // Capital / Goodwill investment per branch
  const [capitalExpenses, setCapitalExpenses] = useState([]);
  const [showCapitalDialog, setShowCapitalDialog] = useState(false);
  const [showCapitalListBranch, setShowCapitalListBranch] = useState(null); // branch object or null
  const [capitalForm, setCapitalForm] = useState({ title: '', category: 'goodwill', description: '', amount: '', branch_id: '', payment_mode: 'cash', date: localToday(), notes: '' });

  const openCapitalDialog = (branch) => {
    setCapitalForm({ title: '', category: 'goodwill', description: '', amount: '', branch_id: branch?.id || '', payment_mode: 'cash', date: localToday(), notes: '' });
    setShowCapitalDialog(true);
  };

  const saveCapitalExpense = async () => {
    if (!capitalForm.title || !capitalForm.amount) { toast.error('Title and amount are required'); return; }
    try {
      await api.post('/capital-expenses', {
        ...capitalForm,
        amount: parseFloat(capitalForm.amount),
        branch_id: capitalForm.branch_id || null,
        date: new Date(capitalForm.date).toISOString(),
      });
      toast.success('Capital expense recorded');
      setShowCapitalDialog(false);
      loadCapitalExpenses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const deleteCapitalExpense = async (id) => {
    if (!window.confirm('Delete this capital expense?')) return;
    try {
      await api.delete(`/capital-expenses/${id}`);
      toast.success('Deleted');
      loadCapitalExpenses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const loadCapitalExpenses = async () => {
    try {
      const res = await api.get('/capital-expenses');
      setCapitalExpenses(res.data || []);
    } catch { /* ignore */ }
  };

  const capitalTotalForBranch = (branchId) =>
    capitalExpenses.filter(c => c.branch_id === branchId).reduce((s, c) => s + (c.amount || 0), 0);

  const capitalExpensesForBranch = (branchId) =>
    capitalExpenses.filter(c => c.branch_id === branchId).sort((a, b) => new Date(b.date) - new Date(a.date));

  // ── Compare Branches ─────────────────────────────────────────────────────
  const _yNow = new Date().getFullYear();
  const _mNow = new Date().getMonth();
  const firstOfMonth = `${_yNow}-${String(_mNow + 1).padStart(2, '0')}-01`;
  const lastOfMonth = toLocalYmd(new Date(_yNow, _mNow + 1, 0));
  const [showCompareDrawer, setShowCompareDrawer] = useState(false);
  const [compareBranchIds, setCompareBranchIds] = useState([]);
  const [compareStart, setCompareStart] = useState(firstOfMonth);
  const [compareEnd, setCompareEnd] = useState(lastOfMonth);
  const [compareData, setCompareData] = useState([]);
  const [compareLoading, setCompareLoading] = useState(false);

  const toggleCompareBranch = (id) => {
    setCompareBranchIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : (prev.length >= 4 ? prev : [...prev, id]));
  };

  const fetchCompareData = useCallback(async () => {
    if (compareBranchIds.length < 2) { setCompareData([]); return; }
    setCompareLoading(true);
    try {
      const res = await api.get('/accounting/branch-summary', { params: { start_date: compareStart, end_date: compareEnd } });
      const chosen = (res.data?.branches || []).filter(b => compareBranchIds.includes(b.branch_id));
      setCompareData(chosen);
    } catch (e) {
      toast.error('Failed to load comparison');
      setCompareData([]);
    } finally {
      setCompareLoading(false);
    }
  }, [compareBranchIds, compareStart, compareEnd]);

  useEffect(() => { if (showCompareDrawer) fetchCompareData(); }, [showCompareDrawer, fetchCompareData]);

  useEffect(() => { fetchData(); loadCapitalExpenses(); }, []);

  const fetchData = async () => {
    try {
      const branchList = await fetchBranches(true);
      api.get('/branches/country-defaults').then(r => setCountryDefaults(r.data)).catch(() => {});
      // Single batch call — one aggregation pass for ALL branches, defaults
      // to the current month server-side (was N× /branches/{id}/summary
      // all-time scans → 120s timeouts).
      if (branchList && branchList.length > 0) {
        try {
          const res = await api.get('/branches/summary-batch');
          setSummaries(res.data?.summaries || {});
          setSummaryWindow({ start_date: res.data?.start_date, end_date: res.data?.end_date });
        } catch { setSummaries({}); }
      }
    } catch { toast.error('Failed to fetch branches'); }
    finally { setLoading(false); }
  };

  const handleToggleAutoOffset = async (branchId, enabled) => {
    setSummaries(prev => ({ ...prev, [branchId]: { ...(prev[branchId] || {}), auto_offset_enabled: enabled } }));
    try {
      const r = await api.put(`/branches/${branchId}/auto-offset`, { enabled });
      const cleared = r.data?.cleared?.created || 0;
      toast.success(enabled
        ? (cleared ? `Auto-net ON — netted ${cleared} balance(s) now` : 'Auto-net ON')
        : 'Auto-net OFF');
      fetchData();
    } catch (e) {
      setSummaries(prev => ({ ...prev, [branchId]: { ...(prev[branchId] || {}), auto_offset_enabled: !enabled } }));
      toast.error(e.response?.data?.detail || 'Failed to update auto-net');
    }
  };

  const handleCountryChange = (code) => {
    const defaults = countryDefaults[code] || {};
    setFormData(prev => ({
      ...prev,
      country_code: code,
      currency_code: prev.currency_code || defaults.currency_code || '',
      tax_rate: prev.tax_rate || (defaults.tax_rate ?? ''),
      tax_name: prev.tax_name || defaults.tax_name || '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      tax_rate: formData.tax_rate !== '' ? parseFloat(formData.tax_rate) : null,
      country_code: formData.country_code || null,
      currency_code: formData.currency_code || null,
      tax_name: formData.tax_name || null,
      // iter877 — Online storefront markup (percent). Empty string → 0.
      online_price_markup_percent: formData.online_price_markup_percent !== '' && formData.online_price_markup_percent != null
        ? parseFloat(formData.online_price_markup_percent) : 0,
    };
    try {
      if (editingBranch) { await api.put(`/branches/${editingBranch.id}`, payload); toast.success('Branch updated'); }
      else { await api.post('/branches', payload); toast.success('Branch added'); }
      setShowDialog(false); resetForm(); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleEdit = (b) => {
    setEditingBranch(b);
    setFormData({
      name: b.name,
      location: b.location || '',
      country_code: b.country_code || '',
      currency_code: b.currency_code || '',
      tax_rate: b.tax_rate ?? '',
      tax_name: b.tax_name || '',
      default_payment_mode: b.default_payment_mode || 'bank',
      online_price_markup_percent: b.online_price_markup_percent ?? '',  // iter877
      business_type: b.business_type || 'restaurant',  // iter916
      branch_logo_url: b.branch_logo_url || '',  // iter921
      ...Object.fromEntries(BRANCH_EXTRA_KEYS.map((k) => [k, b[k] || ''])),
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete branch?')) {
      try { await api.delete(`/branches/${id}`); toast.success('Deleted'); fetchData(); }
      catch { toast.error('Failed'); }
    }
  };

  const handleSetMain = async (branchId) => {
    try {
      await api.put(`/branches/${branchId}/set-main`);
      toast.success('Main branch updated');
      fetchData();
    } catch { toast.error('Failed to set main branch'); }
  };

  const resetForm = () => { setFormData({ ...emptyForm }); setEditingBranch(null); };

  const getCurrencySymbol = (code) => {
    const map = { SAR: 'SR', AED: 'AED', USD: '$', EUR: '\u20ac', GBP: '\u00a3', INR: '\u20b9', PHP: '\u20b1', PKR: 'Rs', BDT: '\u09f3', EGP: 'E\u00a3', QAR: 'QAR', KWD: 'KWD', BHD: 'BHD', OMR: 'OMR', JOD: 'JOD' };
    return map[code] || code || 'SAR';
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64">Loading...</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-stone-700 to-stone-900 flex items-center justify-center shadow-lg shadow-stone-300/50">
              <Store size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="branches-page-title">Branches</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Manage branches across countries with local currency & tax</p>
              {summaryWindow?.start_date && (
                <span className="inline-flex items-center gap-1 mt-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-info/10 text-info border border-info/20" data-testid="summary-window-label" title="Branch totals below are for this period. All-time figures remain in each branch's detail view.">
                  <CalendarDays size={11} /> Totals: {summaryWindow.start_date} → {summaryWindow.end_date} (month-to-date)
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <PageTour storageKey="tour_branches" steps={[
              { target: '[data-testid="main-branch-section"], [data-testid="no-main-branch-hint"]', title: 'Main Branch', content: 'Your main/headquarters branch is displayed at the top. Click the crown icon on any branch to set it as main.' },
              { target: '[data-testid="other-branches-section"]', title: 'Other Branches', content: 'All other branches are shown here horizontally. Each card shows financial summary including sales, expenses, and net profit.' },
              { target: '[data-testid="add-branch-button"]', title: 'Add Branch', content: 'Create new branches with location, country, currency, and tax settings.' },
            ]} />
            <Button variant="outline" className="rounded-full border-info/30 text-info hover:bg-info/10" onClick={() => { setShowCompareDrawer(true); setCompareBranchIds(branches.filter(b => !b.is_main).slice(0, 3).map(b => b.id)); }} data-testid="compare-branches-btn">
              <GitCompare size={16} className="mr-2" />Compare Branches
            </Button>
            <Button variant="outline" className="rounded-full border-success/30 text-success hover:bg-success/10" onClick={() => openCapitalDialog(null)} data-testid="record-capital-top-btn">
              <Coins size={16} className="mr-2" />Record Capital / Goodwill
            </Button>
            <Button
              type="button"
              onClick={() => setShowWizard(true)}
              className="rounded-full bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="open-branch-wizard-btn"
              title="One-click setup with smart defaults"
            >
              <Plus size={16} className="mr-1" />Open new branch
            </Button>
            <BranchWizardDialog
              open={showWizard}
              onClose={() => setShowWizard(false)}
              onCreated={() => fetchData()}
            />
            <Dialog open={showDialog} onOpenChange={(o) => { setShowDialog(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild><Button variant="outline" className="rounded-full" data-testid="add-branch-button-2"><Plus size={18} className="mr-2" />Advanced…</Button></DialogTrigger>
            <DialogContent className="max-w-3xl" data-testid="branch-dialog">
              <DialogHeader><DialogTitle className="font-outfit">{editingBranch ? 'Edit' : 'Add'} Branch</DialogTitle></DialogHeader>
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>Branch Name *</Label><Input value={formData.name} data-testid="branch-name-input" onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder="e.g., Downtown Store" /></div>
                  <div><Label>Location</Label><Input value={formData.location} data-testid="branch-location-input" onChange={(e) => setFormData({ ...formData, location: e.target.value })} placeholder="e.g., Riyadh" /></div>
                  <div>
                    <Label className="flex items-center gap-1"><Globe size={12} />Country</Label>
                    <Select value={formData.country_code} onValueChange={handleCountryChange}>
                      <SelectTrigger data-testid="branch-country-select"><SelectValue placeholder="Select country" /></SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map(c => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="flex items-center gap-1"><DollarSign size={12} />Currency</Label>
                    <Select value={formData.currency_code} onValueChange={v => setFormData({ ...formData, currency_code: v })}>
                      <SelectTrigger data-testid="branch-currency-select"><SelectValue placeholder="Select currency" /></SelectTrigger>
                      <SelectContent>
                        {CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="flex items-center gap-1"><Percent size={12} />Tax Rate (%)</Label>
                    <Input type="number" step="0.1" min="0" max="100" value={formData.tax_rate} data-testid="branch-tax-rate-input" onChange={(e) => setFormData({ ...formData, tax_rate: e.target.value })} placeholder="e.g., 15" />
                  </div>
                  <div className="col-span-2">
                    <Label>Tax Name</Label>
                    <Input value={formData.tax_name} data-testid="branch-tax-name-input" onChange={(e) => setFormData({ ...formData, tax_name: e.target.value })} placeholder="e.g., VAT, GST, Sales Tax" />
                  </div>
                  {/* iter916 — Business model of the branch. Tailor unlocks the
                      POS "Measurements" capture; barber/retail keep POS generic. */}
                  <div className="col-span-2">
                    <Label>Business Type</Label>
                    <Select
                      value={formData.business_type || 'restaurant'}
                      onValueChange={v => setFormData({ ...formData, business_type: v })}
                    >
                      <SelectTrigger data-testid="branch-business-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="restaurant">Restaurant / Cafe</SelectItem>
                        <SelectItem value="tailor">Tailor</SelectItem>
                        <SelectItem value="barber">Barber</SelectItem>
                        <SelectItem value="retail">Retail</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground mt-1">Tailor shows a "Measurements" button on the Cashier POS.</p>
                  </div>
                  {/* iter925 — Upload Logo button (replaces the URL text box).
                      Reuses the branding upload pipeline; the returned URL is
                      saved on THIS branch's branch_logo_url. */}
                  <div className="col-span-2">
                    <Label>Branch Logo</Label>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      id="branch-logo-file"
                      data-testid="branch-logo-file-input"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          // iter926 — Auto-convert: reuse the branding page's
                          // thermalLogoOptimizer (resize to 480px for 80mm paper,
                          // grayscale + dither to monochrome, PNG <30 KB) so the
                          // stored file is clean and small for thermal printers.
                          const opt = await optimiseThermalLogo(file, 80);
                          const blob = await (await fetch(opt.dataUrl)).blob();
                          const fd = new FormData();
                          fd.append('file', new File([blob], 'branch_logo.png', { type: 'image/png' }));
                          const { data } = await api.post('/branches/upload-logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                          setFormData(prev => ({ ...prev, branch_logo_url: data.logo_url }));
                          toast.success(`Logo converted for thermal print · ${formatBytes(opt.originalSizeBytes)} → ${formatBytes(opt.optimisedSizeBytes)}`);
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || err?.message || 'Logo upload failed');
                        } finally {
                          e.target.value = '';
                        }
                      }}
                    />
                    <div className="flex items-center gap-3 mt-1">
                      {formData.branch_logo_url ? (
                        <>
                          <img src={formData.branch_logo_url} alt="Branch logo" className="h-12 w-12 rounded-lg object-contain border bg-white" data-testid="branch-logo-preview" />
                          <Button type="button" size="sm" variant="outline" onClick={() => document.getElementById('branch-logo-file')?.click()} data-testid="branch-logo-replace-btn">Replace Logo</Button>
                          <Button type="button" size="sm" variant="ghost" className="text-error" onClick={() => setFormData(prev => ({ ...prev, branch_logo_url: '' }))} data-testid="branch-logo-remove-btn">Remove</Button>
                        </>
                      ) : (
                        <Button type="button" size="sm" variant="outline" onClick={() => document.getElementById('branch-logo-file')?.click()} data-testid="branch-logo-upload-btn">Upload Logo</Button>
                      )}
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">Auto-converted to a monochrome (B&W) bitmap sized for 80mm thermal printers. Printed on this branch's receipts and PDFs; empty = company logo. Survives "Sync to all branches".</p>
                  </div>
                  {/* iter863 — Default salary payment mode used by the
                      Bulk Salary "Auto-suggest" button as the branch-level fallback. */}
                  <div className="col-span-2">
                    <Label>Default salary payment mode</Label>
                    <Select
                      value={formData.default_payment_mode || 'bank'}
                      onValueChange={v => setFormData({ ...formData, default_payment_mode: v })}
                    >
                      <SelectTrigger data-testid="branch-default-payment-mode"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bank">Bank transfer</SelectItem>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground mt-1">Bulk Salary → "Auto-suggest" uses this when no per-employee preference is set.</p>
                  </div>
                  {/* iter877 — Online storefront markup. Adds a % uplift on
                      top of the item's branch-effective price for orders
                      placed via the self-service online menu. */}
                  <div className="col-span-2">
                    <Label className="flex items-center gap-1"><Percent size={12} />Online order markup (%)</Label>
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      max="100"
                      value={formData.online_price_markup_percent ?? ''}
                      onChange={(e) => setFormData({ ...formData, online_price_markup_percent: e.target.value })}
                      placeholder="e.g., 10 — leave 0 to disable"
                      data-testid="branch-online-markup-input"
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">Applied on the self-service online menu only. In-store POS is unaffected.</p>
                  </div>
                </div>
                <BranchZatcaFields formData={formData} setFormData={setFormData} />
                {formData.country_code && countryDefaults[formData.country_code] && (
                  <div className="p-2 bg-info/10 rounded-lg border border-info/30 text-xs text-info" data-testid="country-defaults-hint">
                    <span className="font-medium">Defaults for {COUNTRIES.find(c => c.code === formData.country_code)?.name}:</span>{' '}
                    {countryDefaults[formData.country_code].currency_code} | {countryDefaults[formData.country_code].tax_name} {countryDefaults[formData.country_code].tax_rate}%
                  </div>
                )}
                <div className="flex gap-3">
                  <Button type="submit" data-testid="submit-branch-button" className="rounded-full">{editingBranch ? 'Update' : 'Add'} Branch</Button>
                  <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="rounded-full">Cancel</Button>
                </div>
              </form>
              {/* iter877 — Live storefront preview panel. Fetches one real
                  menu item on open and re-computes the customer-facing online
                  price locally as the admin edits the markup — no save needed. */}
              <StorefrontPreviewPanel
                branchId={editingBranch?.id}
                markupPercent={parseFloat(formData.online_price_markup_percent) || 0}
                currencyCode={formData.currency_code || countryDefaults[formData.country_code]?.currency_code || 'SAR'}
              />
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Main Branch - Branch with is_main=true displayed prominently */}
        {(() => {
          const mainBranch = branches.find(b => b.is_main);
          if (!mainBranch) {
            // No main branch set - show hint
            if (branches.length > 0) {
              return (
                <div className="p-4 bg-warning/10 border border-warning/30 rounded-lg text-sm text-warning" data-testid="no-main-branch-hint-2">
                  <Crown size={16} className="inline mr-2" />No main branch selected. Click the <strong>crown icon</strong> on any branch below to set it as your main branch.
                </div>
              );
            }
            return null;
          }
          const s = summaries[mainBranch.id];
          const cur = getCurrencySymbol(mainBranch.currency_code);
          const countryName = COUNTRIES.find(c => c.code === mainBranch.country_code)?.name;
          return (
            <div data-testid="main-branch-section-2">
              <div className="flex items-center gap-2 mb-3">
                <Crown size={18} className="text-warning" />
                <h2 className="text-lg font-semibold font-outfit text-foreground">Main Branch</h2>
              </div>
              <BranchCard
                branch={mainBranch}
                summary={s}
                isMain
                currencySymbol={cur}
                countryName={countryName}
                capital={capitalTotalForBranch(mainBranch.id)}
                onCapital={(br) => openCapitalDialog(br)}
                onViewCapitalList={(br) => setShowCapitalListBranch(br)}
                onView={(sum) => setShowDetail(sum)}
                onEdit={(br) => handleEdit(br)}
                onDelete={(id) => handleDelete(id)}
                onToggleAutoOffset={handleToggleAutoOffset}
              />
            </div>
          );
        })()}

        {/* Other Branches */}
        {branches.length > 0 && (
          <div data-testid="other-branches-section-2">
            <div className="flex items-center gap-2 mb-3">
              <Building2 size={18} className="text-muted-foreground" />
              <h2 className="text-lg font-semibold font-outfit text-foreground">{branches.find(b => b.is_main) ? 'Other Branches' : 'All Branches'}</h2>
              <Badge variant="outline" className="text-xs">{branches.filter(b => !b.is_main).length}</Badge>
            </div>
            <div className="flex flex-col gap-4 pb-2" data-testid="other-branches-section-3">
              {branches.filter(b => !b.is_main).map((branch) => {
                const s = summaries[branch.id];
                const cur = getCurrencySymbol(branch.currency_code);
                const countryName = COUNTRIES.find(c => c.code === branch.country_code)?.name;
                return (
                  <BranchCard
                    key={branch.id}
                    branch={branch}
                    summary={s}
                    currencySymbol={cur}
                    countryName={countryName}
                    capital={capitalTotalForBranch(branch.id)}
                    onSetMain={(id) => handleSetMain(id)}
                    onCapital={(br) => openCapitalDialog(br)}
                    onViewCapitalList={(br) => setShowCapitalListBranch(br)}
                    onView={(sum) => setShowDetail(sum)}
                    onEdit={(br) => handleEdit(br)}
                    onDelete={(id) => handleDelete(id)}
                    onToggleAutoOffset={handleToggleAutoOffset}
                  />
                );
              })}
            </div>
          </div>
        )}

        {branches.length === 0 && (
          <Card className="border-dashed border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50/50" data-testid="branches-empty-state">
            <CardContent className="p-10 text-center space-y-4">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-300/50">
                <Sparkles size={28} className="text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold font-outfit text-stone-900">No branches yet</h2>
                <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                  Open your first branch in <strong>30 seconds</strong> — we&apos;ll pre-fill currency, tax, language, and print defaults based on the country you pick.
                </p>
              </div>
              <Button
                onClick={() => setShowWizard(true)}
                className="rounded-full bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-300/40"
                size="lg"
                data-testid="empty-state-open-wizard"
              >
                <Sparkles size={18} className="mr-2" />Open your first branch
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Branch Detail Dialog */}
        <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
          <DialogContent className="max-w-lg" data-testid="branch-detail-dialog">
            <DialogHeader><DialogTitle className="font-outfit">{showDetail?.branch_name} - Details</DialogTitle></DialogHeader>
            {showDetail && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-success/10 rounded-lg"><div className="text-xs text-muted-foreground">Total Sales</div><div className="text-lg font-bold text-success">{showDetail.total_sales.toFixed(2)}</div><div className="text-xs">{showDetail.sales_count} transactions</div></div>
                  <div className="p-3 bg-error/10 rounded-lg"><div className="text-xs text-muted-foreground">Total Expenses</div><div className="text-lg font-bold text-error">{showDetail.total_expenses.toFixed(2)}</div><div className="text-xs">{showDetail.expenses_count} transactions</div></div>
                  <div className="p-3 bg-info/10 rounded-lg"><div className="text-xs text-muted-foreground">Supplier Payments</div><div className="text-lg font-bold text-info">{showDetail.total_supplier_payments.toFixed(2)}</div><div className="text-xs">{showDetail.sp_count} transactions</div></div>
                  <div className="p-3 bg-primary/10 rounded-lg"><div className="text-xs text-muted-foreground">Net Profit</div><div className={`text-lg font-bold ${showDetail.net_profit >= 0 ? 'text-success' : 'text-error'}`}>{showDetail.net_profit.toFixed(2)}</div></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-cash/10 rounded-lg border border-cash/30"><div className="text-xs text-muted-foreground">Cash In Hand</div><div className={`text-lg font-bold ${(showDetail.cash_in_hand || 0) >= 0 ? 'text-cash' : 'text-error'}`}>{(showDetail.cash_in_hand || 0).toFixed(2)}</div></div>
                  <div className="p-3 bg-bank/10 rounded-lg border border-bank/30"><div className="text-xs text-muted-foreground">Bank In Hand</div><div className={`text-lg font-bold ${(showDetail.bank_in_hand || 0) >= 0 ? 'text-bank' : 'text-error'}`}>{(showDetail.bank_in_hand || 0).toFixed(2)}</div></div>
                </div>
                <div className="border-t pt-3">
                  <h4 className="text-sm font-medium mb-2">Sales Breakdown</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                    <div className="p-2 bg-cash/10 rounded text-center"><div className="text-xs text-muted-foreground">Cash</div><div className="text-sm font-bold text-cash">{showDetail.sales_cash.toFixed(2)}</div></div>
                    <div className="p-2 bg-bank/10 rounded text-center"><div className="text-xs text-muted-foreground">Bank</div><div className="text-sm font-bold text-bank">{showDetail.sales_bank.toFixed(2)}</div></div>
                    <div className="p-2 bg-credit/10 rounded text-center"><div className="text-xs text-muted-foreground">Credit</div><div className="text-sm font-bold text-credit">{showDetail.sales_credit.toFixed(2)}</div></div>
                  </div>
                </div>
                {/* iter923 — Bank History timeline (date-based bank switching). */}
                {bankTimeline(showDetail.branch_id).length > 0 && (
                  <div className="border-t pt-3" data-testid="branch-bank-timeline">
                    <h4 className="text-sm font-medium mb-2">Bank History</h4>
                    <div className="space-y-0">
                      {bankTimeline(showDetail.branch_id).map((acc, i, arr) => (
                        <div key={acc.id} className="flex gap-3">
                          <div className="flex flex-col items-center">
                            <span className={`h-3 w-3 rounded-full border-2 shrink-0 mt-0.5 ${acc.until ? 'bg-muted border-muted-foreground/40' : 'bg-success border-success'}`} />
                            {i < arr.length - 1 && <span className="w-px flex-1 bg-border" />}
                          </div>
                          <div className="pb-3 text-xs" data-testid={`bank-timeline-row-${acc.id}`}>
                            <span className="font-semibold">{acc.bank_name || acc.name}</span>
                            <span className="text-muted-foreground"> · {acc.account_number}</span>
                            <div className="text-muted-foreground mt-0.5">
                              {acc.active_from ? `From ${acc.active_from}` : 'From the beginning'}
                              {acc.until ? ` → until ${acc.until}` : ''}
                              {!acc.until && <span className="ml-1 text-success font-medium">· Active now</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="border-t pt-3">
                  <h4 className="text-sm font-medium mb-2">Expenses Breakdown</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-2 bg-cash/10 rounded text-center"><div className="text-xs text-muted-foreground">Cash</div><div className="text-sm font-bold text-cash">{showDetail.expenses_cash.toFixed(2)}</div></div>
                    <div className="p-2 bg-bank/10 rounded text-center"><div className="text-xs text-muted-foreground">Bank</div><div className="text-sm font-bold text-bank">{showDetail.expenses_bank.toFixed(2)}</div></div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Record Capital / Goodwill Dialog */}
        <Dialog open={showCapitalDialog} onOpenChange={setShowCapitalDialog}>
          <DialogContent className="max-w-lg" data-testid="capital-dialog">
            <DialogHeader><DialogTitle className="font-outfit flex items-center gap-2"><Coins size={16} className="text-success" />Record Capital / Goodwill</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground bg-success/10 border border-success/30 rounded-md p-2">
                Use this for one-time branch investment: <strong>goodwill</strong> (paid to acquire the branch), <strong>building/lease purchase</strong>, <strong>equipment</strong>, <strong>renovation / fit-out</strong>, or any other setup cost. These stay separate from daily operating expenses.
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Title *</Label><Input value={capitalForm.title} onChange={(e) => setCapitalForm({ ...capitalForm, title: e.target.value })} placeholder="e.g., Goodwill — Riyadh Branch" data-testid="capital-title-input" /></div>
                <div>
                  <Label>Category</Label>
                  <Select value={capitalForm.category} onValueChange={(v) => setCapitalForm({ ...capitalForm, category: v })}>
                    <SelectTrigger data-testid="capital-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="goodwill">Goodwill (acquisition premium)</SelectItem>
                      <SelectItem value="building">Building / Lease</SelectItem>
                      <SelectItem value="equipment">Equipment & Furniture</SelectItem>
                      <SelectItem value="renovation">Renovation / Fit-out</SelectItem>
                      <SelectItem value="licensing">Licensing & Permits</SelectItem>
                      <SelectItem value="other">Other Setup Cost</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Branch *</Label>
                  <Select value={capitalForm.branch_id || 'none'} onValueChange={(v) => setCapitalForm({ ...capitalForm, branch_id: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="capital-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Amount *</Label><Input type="number" step="0.01" value={capitalForm.amount} onChange={(e) => setCapitalForm({ ...capitalForm, amount: e.target.value })} placeholder="0.00" data-testid="capital-amount-input" /></div>
                <div>
                  <Label>Payment Mode</Label>
                  <Select value={capitalForm.payment_mode} onValueChange={(v) => setCapitalForm({ ...capitalForm, payment_mode: v })}>
                    <SelectTrigger data-testid="capital-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="bank">Bank</SelectItem>
                      <SelectItem value="loan">Financed (Loan)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Date</Label><Input type="date" min="2018-01-01" value={capitalForm.date} onChange={(e) => setCapitalForm({ ...capitalForm, date: e.target.value })} data-testid="capital-date-input" /></div>
                <div className="col-span-2"><Label>Description / Notes</Label><Input value={capitalForm.description} onChange={(e) => setCapitalForm({ ...capitalForm, description: e.target.value })} placeholder="e.g., Paid to previous owner for Al Khobar shop transfer" data-testid="capital-desc-input" /></div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setShowCapitalDialog(false)}>Cancel</Button>
                <Button type="button" className="rounded-xl bg-success hover:bg-emerald-700 text-white" onClick={saveCapitalExpense} data-testid="capital-save-btn">Save Capital Expense</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Capital Breakdown Dialog */}
        <Dialog open={!!showCapitalListBranch} onOpenChange={(o) => !o && setShowCapitalListBranch(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle className="font-outfit flex items-center gap-2"><Wallet size={16} className="text-success" />Capital Investment — {showCapitalListBranch?.name}</DialogTitle></DialogHeader>
            {showCapitalListBranch && (() => {
              const items = capitalExpensesForBranch(showCapitalListBranch.id);
              const total = items.reduce((s, c) => s + (c.amount || 0), 0);
              const byCat = items.reduce((acc, c) => { acc[c.category] = (acc[c.category] || 0) + (c.amount || 0); return acc; }, {});
              const cur = getCurrencySymbol(showCapitalListBranch.currency_code);
              return (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                    {['goodwill', 'building', 'equipment', 'renovation', 'licensing', 'other'].map(cat => (
                      <div key={cat} className="p-2 rounded-lg bg-success/10 border border-success/30 text-center">
                        <div className="text-[10px] uppercase tracking-wide text-success">{cat}</div>
                        <div className="text-sm font-bold text-success">{cur} {(byCat[cat] || 0).toFixed(0)}</div>
                      </div>
                    ))}
                  </div>
                  <div className="p-3 bg-foreground text-white rounded-lg flex justify-between items-center">
                    <span className="text-sm font-medium">Total Capital Invested</span>
                    <span className="text-lg font-bold">{cur} {total.toFixed(2)}</span>
                  </div>
                  <div className="max-h-72 overflow-y-auto border border-border rounded-lg">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50 sticky top-0">
                          <tr className="text-left">
                            <th className="p-2 font-medium">Date</th>
                            <th className="p-2 font-medium">Title</th>
                            <th className="p-2 font-medium">Category</th>
                            <th className="p-2 font-medium">Mode</th>
                            <th className="p-2 font-medium text-right">Amount</th>
                            <th className="p-2"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No capital expenses recorded yet.</td></tr>}
                          {items.map(c => (
                            <tr key={c.id} className="border-t hover:bg-muted/50">
                              <td className="p-2">{new Date(c.date).toLocaleDateString()}</td>
                              <td className="p-2 font-medium">{c.title}</td>
                              <td className="p-2"><Badge variant="secondary" className="capitalize">{c.category}</Badge></td>
                              <td className="p-2"><Badge variant="outline" className="capitalize">{c.payment_mode}</Badge></td>
                              <td className="p-2 text-right font-mono font-semibold">{cur} {c.amount.toFixed(2)}</td>
                              <td className="p-2 text-right"><Button size="sm" variant="ghost" className="h-7 text-error" onClick={() => deleteCapitalExpense(c.id)}><Trash2 size={12} /></Button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end pt-1">
                    <Button variant="outline" onClick={() => setShowCapitalListBranch(null)}>Close</Button>
                    <Button className="rounded-xl bg-success hover:bg-emerald-700 text-white" onClick={() => { openCapitalDialog(showCapitalListBranch); setShowCapitalListBranch(null); }}>
                      <Plus size={14} className="mr-1" />Add Another
                    </Button>
                  </div>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* Compare Branches Drawer */}
        <Sheet open={showCompareDrawer} onOpenChange={setShowCompareDrawer}>
          <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="font-outfit flex items-center gap-2">
                <GitCompare size={18} className="text-info" /> Compare Branches
              </SheetTitle>
            </SheetHeader>
            <div className="mt-5 space-y-5">
              {/* Date range */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>From</Label>
                  <Input type="date" min="2018-01-01" value={compareStart} onChange={(e) => setCompareStart(e.target.value)} data-testid="compare-start-date" />
                </div>
                <div>
                  <Label>To</Label>
                  <Input type="date" min="2018-01-01" value={compareEnd} onChange={(e) => setCompareEnd(e.target.value)} data-testid="compare-end-date" />
                </div>
              </div>

              {/* Quick date chips */}
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setCompareStart(firstOfMonth); setCompareEnd(lastOfMonth); }}>This Month</Button>
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => {
                  const d = new Date(_yNow, _mNow - 1, 1);
                  setCompareStart(toLocalYmd(d));
                  setCompareEnd(toLocalYmd(new Date(_yNow, _mNow, 0)));
                }}>Last Month</Button>
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setCompareStart(`${_yNow}-01-01`); setCompareEnd(`${_yNow}-12-31`); }}>This Year</Button>
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setCompareStart(`${_yNow - 1}-01-01`); setCompareEnd(`${_yNow - 1}-12-31`); }}>Last Year</Button>
              </div>

              {/* Branch pickers */}
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Pick 2-4 branches</Label>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {branches.map(b => {
                    const active = compareBranchIds.includes(b.id);
                    const disabled = !active && compareBranchIds.length >= 4;
                    return (
                      <label key={b.id} className={`flex items-center gap-2 p-2 border rounded-lg cursor-pointer transition ${active ? 'border-info bg-info/10' : 'border-border'} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}>
                        <Checkbox checked={active} disabled={disabled} onCheckedChange={() => toggleCompareBranch(b.id)} data-testid={`compare-pick-${b.id}`} />
                        <span className="text-sm font-medium">{b.name}</span>
                        {b.is_main && <Crown size={12} className="text-warning" />}
                        {/* iter689 — Country flag chip from the shared
                            registry. Hidden when no country is set. */}
                        {b.country_code && getCountry(b.country_code) && (
                          <span className="text-base leading-none" title={getCountry(b.country_code).name}>{getCountry(b.country_code).flag}</span>
                        )}
                        <span className="ml-auto text-[10px] text-muted-foreground">{b.country_code || '—'}</span>
                      </label>
                    );
                  })}
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">{compareBranchIds.length} of 4 selected</div>
              </div>

              <Button type="button" className="w-full" onClick={fetchCompareData} disabled={compareBranchIds.length < 2 || compareLoading} data-testid="compare-run-btn">
                {compareLoading ? 'Loading…' : 'Refresh Comparison'}
              </Button>

              {/* Results */}
              {compareData.length >= 2 && (() => {
                const chartData = compareData.map(b => ({
                  name: b.branch_name,
                  Sales: b.sales.total,
                  Expenses: b.expenses_total,
                  'Net Profit': b.net,
                  Cash: b.cash_position?.cash_in_hand ?? 0,
                  Bank: b.cash_position?.bank_in_hand ?? 0,
                }));
                return (
                  <>
                    <div className="rounded-xl border border-border bg-white p-4">
                      <div className="text-xs font-semibold text-foreground mb-2">Sales vs Expenses vs Net Profit</div>
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                          <Tooltip formatter={(v) => `${cur()} ${Number(v).toLocaleString()}`} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Bar dataKey="Sales" fill="#10b981" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="Expenses" fill="#ef4444" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="Net Profit" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="rounded-xl border border-border bg-white p-4">
                      <div className="text-xs font-semibold text-foreground mb-2">Cash Position (Cash + Bank)</div>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                          <Tooltip formatter={(v) => `${cur()} ${Number(v).toLocaleString()}`} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Bar dataKey="Cash" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="Bank" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    {/* Comparison table */}
                    <div className="overflow-x-auto rounded-xl border border-border">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-xs">
                          <tr>
                            <th className="p-2 text-left">Metric</th>
                            {compareData.map(b => <th key={b.branch_id} className="p-2 text-right">{b.branch_name}</th>)}
                          </tr>
                        </thead>
                        <tbody className="text-xs font-mono">
                          {[
                            ['Sales', compareData.map(b => b.sales.total), 'text-success'],
                            ['Expenses', compareData.map(b => b.expenses_total), 'text-error'],
                            ['COGS', compareData.map(b => b.cogs), 'text-warning'],
                            ['Net Profit', compareData.map(b => b.net), null],
                            ['Margin %', compareData.map(b => `${b.net_margin}%`), null],
                            ['Cash in Hand', compareData.map(b => b.cash_position?.cash_in_hand ?? 0), 'text-cash'],
                            ['Bank in Hand', compareData.map(b => b.cash_position?.bank_in_hand ?? 0), 'text-bank'],
                            ['Customer Due', compareData.map(b => b.cash_position?.customer_receivable ?? 0), 'text-warning'],
                          ].map(([label, values, color]) => (
                            <tr key={label} className="border-t hover:bg-muted/50">
                              <td className="p-2 font-sans text-foreground">{label}</td>
                              {values.map((v, i) => (
                                <td key={i} className={`p-2 text-right ${color || ''}`}>
                                  {typeof v === 'number' ? `${cur()} ${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : v}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })()}
              {compareBranchIds.length < 2 && (
                <div className="rounded-xl bg-muted/50 border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                  Pick at least 2 branches to compare.
                </div>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </DashboardLayout>
  );
}
